/**
 * Service pour gérer les traductions multilingues
 */

import { WidgetLanguage } from "../types/widgets.d";
import { LibelleMultilingue } from "../types/apidae.d";

export class TranslationService {
  /**
   * Extrait le libellé dans la langue demandée
   */
  static getLabel(
    libelle: LibelleMultilingue | undefined,
    language: WidgetLanguage,
  ): string {
    if (!libelle) return "";

    // Map des clés de libellé selon la langue
    const languageKeys: Record<WidgetLanguage, keyof LibelleMultilingue> = {
      [WidgetLanguage.FR]: "libelleFr",
      [WidgetLanguage.EN]: "libelleEn",
      [WidgetLanguage.ES]: "libelleEs",
      [WidgetLanguage.IT]: "libelleIt",
      [WidgetLanguage.DE]: "libelleDe",
      [WidgetLanguage.NL]: "libelleNl",
      [WidgetLanguage.RU]: "libelleRu",
      [WidgetLanguage.ZH]: "libelleZh",
      [WidgetLanguage.PT_BR]: "libellePtBR",
      [WidgetLanguage.JA]: "libelleJa",
      [WidgetLanguage.KO]: "libelleKo",
    };

    const key = languageKeys[language];
    const value = libelle[key];

    // Fallback sur le français si la traduction n'existe pas
    return value || libelle.libelleFr || "";
  }

  /**
   * Extrait le libellé avec fallback automatique
   */
  static getLabelWithFallback(
    libelle: LibelleMultilingue | undefined,
    language: WidgetLanguage,
    fallbackLanguages: WidgetLanguage[] = [
      WidgetLanguage.FR,
      WidgetLanguage.EN,
    ],
  ): string {
    if (!libelle) return "";

    // Essayer la langue demandée
    const primary = this.getLabel(libelle, language);
    if (primary) return primary;

    // Essayer les fallbacks
    for (const fallback of fallbackLanguages) {
      const value = this.getLabel(libelle, fallback);
      if (value) return value;
    }

    // Retourner la première valeur disponible
    return Object.values(libelle).find((v) => v) || "";
  }

  /**
   * Traduit les chaînes de l'interface
   */
  static translate(key: string, language: WidgetLanguage): string {
    const translations: Record<WidgetLanguage, Record<string, string>> = {
      [WidgetLanguage.FR]: {
        loading: "Chargement...",
        error: "Erreur",
        noResults: "Aucun résultat",
        sortBy: "Trier par",
        filters: "Filtres",
        filterBy: "Filtrer par :",
        reset: "Réinitialiser",
        apply: "Appliquer",
        name: "Nom",
        commune: "Commune",
        date: "Date",
        previous: "Précédent",
        next: "Suivant",
        showMore: "Voir plus",
        showLess: "Voir moins",
        seeAll: "Voir tout",
        seeMore: "Voir plus",
        readMore: "Lire la suite",
        loadMore: "Charger plus",
        website: "Site web",
        openingHours: "Horaires",
        contact: "Contact",
        description: "Description",
        informations: "Informations",
        toggleFilters: "Afficher/Masquer les filtres",
        clear: "Effacer",
        clearFilters: "Effacer les filtres",
        list: "Liste",
        map: "Carte",
        agenda: "Agenda",
        searchInThisArea: "Rechercher dans cette zone",
        results: "Résultats",
        result: "Résultat",
        duration: "Durée",
        free: "Gratuit",
        animals: "Animaux",
        animalsAccepted: "Animaux acceptés.",
        animalsAcceptedWithSupplement: "Animaux acceptés, avec suppléments.",
        animalsAcceptedWithoutSupplement: "Animaux acceptés, sans suppléments.",
        animalsNotAccepted: "Animaux non acceptés.",
        roomRental: "Location de salles",
        modularRoomsCount: "Nombre de salles modulables",
        equippedMeetingRooms: "Salles de réunion équipées",
        maxCapacity: "Capacité d'accueil max",
        accessibility: "Accessibilité",
        tourismAndDisability: "Tourisme et Handicap",
        viewAllAccessibilityCriteria: "Voir tous les critères d'accessibilité",
        descriptifHandicapMoteur: "Descriptif handicap moteur",
        descriptifHandicapAuditif: "Descriptif handicap auditif",
        descriptifHandicapVisuel: "Descriptif handicap visuel",
        descriptifHandicapMental: "Descriptif handicap mental",
        classification: "Classement",
        activities: "Activités",
        type: "Type",
        audience: "Public",
        labels: "Labels",
        labelsAndChaines: "Labels & Chaînes",
        extraDocumentation: "Documentation supplémentaire",
        photoVisit: "Visite photos",
        backToFiche: "Retour à la fiche",
        distance: "Distance",
        itinerary: "Itinéraire",
        altitudeMaximum: "Altitude max.",
        altitudeMinimum: "Altitude min.",
        altitude: "Altitude",
        denivellationPositive: "Dénivellation positive",
        denivellationNegative: "Dénivellation négative",
        precisionsBalisage: "Précisions balisage",
        balisage: "Balisage",
        opening: "Ouverture",
        openings: "Ouvertures",
        pricing: "Tarifs",
        paymentMethods: "Moyens de paiement",
        exceptionalClosurePrefix: "Fermeture exceptionnelle ",
        closed: "Fermé",
        open: "Ouvert",
        reservation: "Réservation",
        reserve: "Réserver",
        viewOnMap: "Voir sur la carte",
        dateStartPlaceholder: "Date de début",
        dateEndPlaceholder: "Date de fin",
        communePlaceholder: "Nom de la commune",
        selected: "sélectionné",
        selectedPlural: "sélectionnés",
        dateFrom: "De",
        dateTo: "À",
        periodFrom: "Du",
        periodTo: "au",
        dayOrdinal1: "1er",
        dayOrdinal2: "2",
        dayOrdinal3: "3",
        exceptionalClosurePrefixSingular: "Fermeture exceptionnelle le ",
        exceptionalClosurePrefixPlural: "Fermeture exceptionnelle les ",
        clearDateStart: "Effacer la date de début",
        clearDateEnd: "Effacer la date de fin",
        clearSearch: "Effacer la recherche",
        noResultsFound: "Aucun résultat trouvé.",
        adjustSearchWithFilters: "Ajustez votre recherche grâce aux filtres.",
        noEventsWithDate: "Aucun événement avec date disponible",
        viewDetail: "Voir le détail",
        widgetErrorTitle: "Erreur de récupération du widget Apidae.",
        widgetErrorDescription:
          "Ce widget n'est pas autorisé pour ce site internet. Veuillez modifier sa configuration ou modifier son intégration.",
        capabilities: "Capacités",
        capacitesUnitesHebergementDeclarees:
          "Nombre d'unités d'hébergements déclarées",
        capacitesPersonnesDeclarees: "Capacité déclarée en nombre de personnes",
        capacitesNombreChambres: "Nombre de chambres",
        capacitesNombreAppartements: "Nombre d'appartements",
        capacitesNombreDortoirs: "Nombre de dortoirs",
        capacitesNombreVillas: "Nombre de villas",
        capacitesPersonnesClassees: "Capacité classée en nombre de personnes",
        capacitesNombreCabines: "Nombre de cabines",
        capacitesNombreLitsDoubles: "Nombre de lits doubles",
        capacitesNombreLitsSimples: "Nombre de lits simples",
        capacitesSurface: "Surface",
        capacitesNombreSallesDeBain: "Nombre de salles de bain",
        capacitesUnitesHebergementClassees:
          "Nombre d'unités d'hébergements classées",
        capacitesSuperficie: "Superficie",
        capacitesNombreChambresSimples: "Nombre de chambres simples",
        capacitesNombreChambresDoubles: "Nombre de chambres doubles",
        capacitesNombreSuites: "Nombre de suites",
        capacitesPmrUnitesHebergement:
          "Nombre d'unités d'hébergement pour personnes à mobilité réduite",
        capacitesUnitHebergements: "hébergements",
        capacitesUnitPersonnes: "personnes",
        capacitesUnitChambres: "chambres",
        capacitesUnitAppartements: "appartements",
        capacitesUnitDortoirs: "dortoirs",
        capacitesUnitVillas: "villas",
        capacitesUnitCabines: "cabines",
        capacitesUnitLits: "lits",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "salles de bain",
        capacitesUnitEmplacements: "emplacements",
        capacitesUnitSuites: "suites",
      },
      [WidgetLanguage.EN]: {
        loading: "Loading...",
        error: "Error",
        noResults: "No results",
        sortBy: "Sort by",
        filters: "Filters",
        filterBy: "Filter by:",
        reset: "Reset",
        apply: "Apply",
        name: "Name",
        commune: "City",
        date: "Date",
        previous: "Previous",
        next: "Next",
        showMore: "Show more",
        showLess: "Show less",
        seeAll: "See all",
        seeMore: "See more",
        readMore: "Read more",
        loadMore: "Load more",
        website: "Website",
        openingHours: "Opening hours",
        contact: "Contact",
        description: "Description",
        informations: "Information",
        toggleFilters: "Show/Hide filters",
        clear: "Clear",
        clearFilters: "Clear filters",
        list: "List",
        map: "Map",
        agenda: "Agenda",
        searchInThisArea: "Search in this area",
        results: "Results",
        result: "Result",
        duration: "Duration",
        free: "Free",
        animals: "Animals",
        animalsAccepted: "Animals accepted.",
        animalsAcceptedWithSupplement: "Animals accepted, with supplements.",
        animalsAcceptedWithoutSupplement:
          "Animals accepted, without supplements.",
        animalsNotAccepted: "Animals not accepted.",
        roomRental: "Room rental",
        modularRoomsCount: "Number of modular rooms",
        equippedMeetingRooms: "Equipped meeting rooms",
        maxCapacity: "Max. capacity",
        accessibility: "Accessibility",
        tourismAndDisability: "Tourism and Disability",
        viewAllAccessibilityCriteria: "View all accessibility criteria",
        descriptifHandicapMoteur: "Motor disability description",
        descriptifHandicapAuditif: "Hearing disability description",
        descriptifHandicapVisuel: "Visual disability description",
        descriptifHandicapMental: "Mental disability description",
        classification: "Classification",
        activities: "Activities",
        type: "Type",
        audience: "Audience",
        labels: "Labels",
        labelsAndChaines: "Labels & Chains",
        extraDocumentation: "Extra documentation",
        photoVisit: "Photo tour",
        backToFiche: "Back to listing",
        distance: "Distance",
        itinerary: "Itinerary",
        altitudeMaximum: "Max. altitude",
        altitudeMinimum: "Min. altitude",
        altitude: "Altitude",
        denivellationPositive: "Positive elevation",
        denivellationNegative: "Negative elevation",
        precisionsBalisage: "Waymarking details",
        balisage: "Waymarking",
        opening: "Opening",
        openings: "Opening hours",
        pricing: "Pricing",
        paymentMethods: "Payment methods",
        exceptionalClosurePrefix: "Exceptional closure ",
        closed: "Closed",
        open: "Open",
        reservation: "Reservation",
        reserve: "Book",
        viewOnMap: "View on map",
        dateStartPlaceholder: "Start date",
        dateEndPlaceholder: "End date",
        communePlaceholder: "City name",
        selected: "selected",
        selectedPlural: "selected",
        dateFrom: "From",
        dateTo: "To",
        periodFrom: "From",
        periodTo: "to",
        dayOrdinal1: "1st",
        dayOrdinal2: "2nd",
        dayOrdinal3: "3rd",
        exceptionalClosurePrefixSingular: "Exceptional closure on ",
        exceptionalClosurePrefixPlural: "Exceptional closure on ",
        clearDateStart: "Clear start date",
        clearDateEnd: "Clear end date",
        clearSearch: "Clear search",
        noResultsFound: "No results found.",
        adjustSearchWithFilters: "Adjust your search using the filters.",
        noEventsWithDate: "No events with available date",
        viewDetail: "View details",
        widgetErrorTitle: "Error retrieving Apidae widget.",
        widgetErrorDescription:
          "This widget is not authorized for this website. Please modify its configuration or modify its integration.",
        capabilities: "Capacities",
        capacitesUnitesHebergementDeclarees: "Declared accommodation units",
        capacitesPersonnesDeclarees: "Declared capacity (number of people)",
        capacitesNombreChambres: "Number of rooms",
        capacitesNombreAppartements: "Number of apartments",
        capacitesNombreDortoirs: "Number of dormitories",
        capacitesNombreVillas: "Number of villas",
        capacitesPersonnesClassees: "Classified capacity (number of people)",
        capacitesNombreCabines: "Number of cabins",
        capacitesNombreLitsDoubles: "Number of double beds",
        capacitesNombreLitsSimples: "Number of single beds",
        capacitesSurface: "Surface area",
        capacitesNombreSallesDeBain: "Number of bathrooms",
        capacitesUnitesHebergementClassees: "Classified accommodation units",
        capacitesSuperficie: "Area",
        capacitesNombreChambresSimples: "Number of single rooms",
        capacitesNombreChambresDoubles: "Number of double rooms",
        capacitesNombreSuites: "Number of suites",
        capacitesPmrUnitesHebergement:
          "Accommodation units for people with reduced mobility",
        capacitesUnitHebergements: "accommodation units",
        capacitesUnitPersonnes: "people",
        capacitesUnitChambres: "rooms",
        capacitesUnitAppartements: "apartments",
        capacitesUnitDortoirs: "dormitories",
        capacitesUnitVillas: "villas",
        capacitesUnitCabines: "cabins",
        capacitesUnitLits: "beds",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "bathrooms",
        capacitesUnitEmplacements: "pitches",
        capacitesUnitSuites: "suites",
      },
      [WidgetLanguage.ES]: {
        loading: "Cargando...",
        error: "Error",
        noResults: "Sin resultados",
        sortBy: "Ordenar por",
        filters: "Filtros",
        filterBy: "Filtrar por:",
        reset: "Restablecer",
        apply: "Aplicar",
        name: "Nombre",
        commune: "Ciudad",
        date: "Fecha",
        previous: "Anterior",
        next: "Siguiente",
        showMore: "Ver más",
        showLess: "Ver menos",
        seeAll: "Ver todo",
        seeMore: "Ver más",
        readMore: "Leer más",
        loadMore: "Cargar más",
        website: "Sitio web",
        openingHours: "Horario",
        contact: "Contacto",
        description: "Descripción",
        informations: "Información",
        toggleFilters: "Mostrar/Ocultar filtros",
        clear: "Limpiar",
        clearFilters: "Limpiar filtros",
        list: "Lista",
        map: "Mapa",
        agenda: "Agenda",
        searchInThisArea: "Buscar en esta zona",
        results: "Resultados",
        result: "Resultado",
        duration: "Duración",
        free: "Gratis",
        animals: "Animales",
        animalsAccepted: "Animales aceptados.",
        animalsAcceptedWithSupplement: "Animales aceptados, con suplementos.",
        animalsAcceptedWithoutSupplement:
          "Animales aceptados, sin suplementos.",
        animalsNotAccepted: "Animales no aceptados.",
        roomRental: "Alquiler de salas",
        modularRoomsCount: "Número de salas modulables",
        equippedMeetingRooms: "Salas de reunión equipadas",
        maxCapacity: "Capacidad máxima",
        accessibility: "Accesibilidad",
        tourismAndDisability: "Turismo y Discapacidad",
        viewAllAccessibilityCriteria:
          "Ver todos los criterios de accesibilidad",
        descriptifHandicapMoteur: "Descripción discapacidad motora",
        descriptifHandicapAuditif: "Descripción discapacidad auditiva",
        descriptifHandicapVisuel: "Descripción discapacidad visual",
        descriptifHandicapMental: "Descripción discapacidad mental",
        classification: "Clasificación",
        activities: "Actividades",
        type: "Tipo",
        audience: "Público",
        labels: "Etiquetas",
        labelsAndChaines: "Etiquetas y cadenas",
        extraDocumentation: "Documentación adicional",
        photoVisit: "Visita fotos",
        backToFiche: "Volver a la ficha",
        distance: "Distancia",
        itinerary: "Recorrido",
        altitudeMaximum: "Altitud máx.",
        altitudeMinimum: "Altitud mín.",
        altitude: "Altitud",
        denivellationPositive: "Desnivel positivo",
        denivellationNegative: "Desnivel negativo",
        precisionsBalisage: "Precisiones de balización",
        balisage: "Balización",
        opening: "Apertura",
        openings: "Horarios",
        pricing: "Precios",
        paymentMethods: "Medios de pago",
        exceptionalClosurePrefix: "Cierre excepcional ",
        closed: "Cerrado",
        open: "Abierto",
        reservation: "Reserva",
        reserve: "Reservar",
        viewOnMap: "Ver en el mapa",
        dateStartPlaceholder: "Fecha de inicio",
        dateEndPlaceholder: "Fecha de fin",
        communePlaceholder: "Nombre de la ciudad",
        selected: "seleccionado",
        selectedPlural: "seleccionados",
        dateFrom: "De",
        dateTo: "A",
        periodFrom: "Del",
        periodTo: "al",
        dayOrdinal1: "1.º",
        dayOrdinal2: "2.º",
        dayOrdinal3: "3.º",
        exceptionalClosurePrefixSingular: "Cierre excepcional el ",
        exceptionalClosurePrefixPlural: "Cierre excepcional los ",
        clearDateStart: "Borrar fecha de inicio",
        clearDateEnd: "Borrar fecha de fin",
        clearSearch: "Borrar búsqueda",
        noResultsFound: "No se encontraron resultados.",
        adjustSearchWithFilters: "Ajuste su búsqueda usando los filtros.",
        noEventsWithDate: "No hay eventos con fecha disponible",
        viewDetail: "Ver detalles",
        widgetErrorTitle: "Error al recuperar el widget Apidae.",
        widgetErrorDescription:
          "Este widget no está autorizado para este sitio web. Por favor, modifique su configuración o modifique su integración.",
        capabilities: "Capacidades",
        capacitesUnitesHebergementDeclarees:
          "Unidades de alojamiento declaradas",
        capacitesPersonnesDeclarees: "Capacidad declarada (personas)",
        capacitesNombreChambres: "Número de habitaciones",
        capacitesNombreAppartements: "Número de apartamentos",
        capacitesNombreDortoirs: "Número de dormitorios",
        capacitesNombreVillas: "Número de villas",
        capacitesPersonnesClassees: "Capacidad clasificada (personas)",
        capacitesNombreCabines: "Número de cabañas",
        capacitesNombreLitsDoubles: "Número de camas dobles",
        capacitesNombreLitsSimples: "Número de camas individuales",
        capacitesSurface: "Superficie",
        capacitesNombreSallesDeBain: "Número de baños",
        capacitesUnitesHebergementClassees:
          "Unidades de alojamiento clasificadas",
        capacitesSuperficie: "Superficie",
        capacitesNombreChambresSimples: "Número de habitaciones individuales",
        capacitesNombreChambresDoubles: "Número de habitaciones dobles",
        capacitesNombreSuites: "Número de suites",
        capacitesPmrUnitesHebergement:
          "Unidades de alojamiento para personas con movilidad reducida",
        capacitesUnitHebergements: "unidades de alojamiento",
        capacitesUnitPersonnes: "personas",
        capacitesUnitChambres: "habitaciones",
        capacitesUnitAppartements: "apartamentos",
        capacitesUnitDortoirs: "dormitorios",
        capacitesUnitVillas: "villas",
        capacitesUnitCabines: "cabañas",
        capacitesUnitLits: "camas",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "baños",
        capacitesUnitEmplacements: "emplazamientos",
        capacitesUnitSuites: "suites",
      },
      [WidgetLanguage.IT]: {
        loading: "Caricamento...",
        error: "Errore",
        noResults: "Nessun risultato",
        sortBy: "Ordina per",
        filters: "Filtri",
        filterBy: "Filtra per:",
        reset: "Ripristina",
        apply: "Applica",
        name: "Nome",
        commune: "Comune",
        date: "Data",
        previous: "Precedente",
        next: "Successivo",
        showMore: "Mostra di più",
        showLess: "Mostra di meno",
        seeAll: "Vedi tutto",
        seeMore: "Vedi di più",
        readMore: "Leggi di più",
        loadMore: "Carica altro",
        website: "Sito web",
        openingHours: "Orari",
        contact: "Contatto",
        description: "Descrizione",
        informations: "Informazione",
        toggleFilters: "Mostra/Nascondi filtri",
        clear: "Cancella",
        clearFilters: "Cancella filtri",
        list: "Elenco",
        map: "Mappa",
        agenda: "Agenda",
        searchInThisArea: "Cerca in questa zona",
        results: "Risultati",
        result: "Risultato",
        duration: "Durata",
        free: "Gratuito",
        animals: "Animali",
        animalsAccepted: "Animali accettati.",
        animalsAcceptedWithSupplement: "Animali accettati, con supplementi.",
        animalsAcceptedWithoutSupplement:
          "Animali accettati, senza supplementi.",
        animalsNotAccepted: "Animali non accettati.",
        roomRental: "Noleggio sale",
        modularRoomsCount: "Numero di sale modulari",
        equippedMeetingRooms: "Sale riunioni attrezzate",
        maxCapacity: "Capacità massima",
        accessibility: "Accessibilità",
        tourismAndDisability: "Turismo e Disabilità",
        viewAllAccessibilityCriteria:
          "Visualizza tutti i criteri di accessibilità",
        descriptifHandicapMoteur: "Descrizione handicap motorio",
        descriptifHandicapAuditif: "Descrizione handicap uditivo",
        descriptifHandicapVisuel: "Descrizione handicap visivo",
        descriptifHandicapMental: "Descrizione handicap mentale",
        classification: "Classificazione",
        activities: "Attività",
        type: "Tipo",
        audience: "Pubblico",
        labels: "Etichette",
        labelsAndChaines: "Etichette e catene",
        extraDocumentation: "Documentazione supplementare",
        photoVisit: "Visita fotografica",
        backToFiche: "Torna alla scheda",
        distance: "Distanza",
        itinerary: "Itinerario",
        altitudeMaximum: "Altitudine max.",
        altitudeMinimum: "Altitudine min.",
        altitude: "Altitudine",
        denivellationPositive: "Dislivello positivo",
        denivellationNegative: "Dislivello negativo",
        precisionsBalisage: "Precisazioni segnaletica",
        balisage: "Segnaletica",
        opening: "Apertura",
        openings: "Orari",
        pricing: "Prezzi",
        paymentMethods: "Metodi di pagamento",
        exceptionalClosurePrefix: "Chiusura eccezionale ",
        closed: "Chiuso",
        open: "Aperto",
        reservation: "Prenotazione",
        reserve: "Prenota",
        viewOnMap: "Vedi sulla mappa",
        dateStartPlaceholder: "Data di inizio",
        dateEndPlaceholder: "Data di fine",
        communePlaceholder: "Nome del comune",
        selected: "selezionato",
        selectedPlural: "selezionati",
        dateFrom: "Da",
        dateTo: "A",
        periodFrom: "Dal",
        periodTo: "al",
        dayOrdinal1: "1º",
        dayOrdinal2: "2º",
        dayOrdinal3: "3º",
        exceptionalClosurePrefixSingular: "Chiusura eccezionale il ",
        exceptionalClosurePrefixPlural: "Chiusura eccezionale i ",
        clearDateStart: "Cancella data di inizio",
        clearDateEnd: "Cancella data di fine",
        clearSearch: "Cancella ricerca",
        noResultsFound: "Nessun risultato trovato.",
        adjustSearchWithFilters: "Regola la tua ricerca utilizzando i filtri.",
        noEventsWithDate: "Nessun evento con data disponibile",
        viewDetail: "Vedi dettagli",
        widgetErrorTitle: "Errore nel recupero del widget Apidae.",
        widgetErrorDescription:
          "Questo widget non è autorizzato per questo sito web. Si prega di modificare la sua configurazione o modificare la sua integrazione.",
        capabilities: "Capacità",
        capacitesUnitesHebergementDeclarees: "Unità di alloggio dichiarate",
        capacitesPersonnesDeclarees: "Capacità dichiarata (persone)",
        capacitesNombreChambres: "Numero di camere",
        capacitesNombreAppartements: "Numero di appartamenti",
        capacitesNombreDortoirs: "Numero di dormitori",
        capacitesNombreVillas: "Numero di ville",
        capacitesPersonnesClassees: "Capacità classificata (persone)",
        capacitesNombreCabines: "Numero di cabine",
        capacitesNombreLitsDoubles: "Numero di letti doppi",
        capacitesNombreLitsSimples: "Numero di letti singoli",
        capacitesSurface: "Superficie",
        capacitesNombreSallesDeBain: "Numero di bagni",
        capacitesUnitesHebergementClassees: "Unità di alloggio classificate",
        capacitesSuperficie: "Superficie",
        capacitesNombreChambresSimples: "Numero di camere singole",
        capacitesNombreChambresDoubles: "Numero di camere doppie",
        capacitesNombreSuites: "Numero di suite",
        capacitesPmrUnitesHebergement:
          "Unità di alloggio per persone a mobilità ridotta",
        capacitesUnitHebergements: "unità abitative",
        capacitesUnitPersonnes: "persone",
        capacitesUnitChambres: "camere",
        capacitesUnitAppartements: "appartamenti",
        capacitesUnitDortoirs: "dormitori",
        capacitesUnitVillas: "ville",
        capacitesUnitCabines: "cabine",
        capacitesUnitLits: "letti",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "bagni",
        capacitesUnitEmplacements: "piazzole",
        capacitesUnitSuites: "suite",
      },
      [WidgetLanguage.DE]: {
        loading: "Laden...",
        error: "Fehler",
        noResults: "Keine Ergebnisse",
        sortBy: "Sortieren nach",
        filters: "Filter",
        filterBy: "Filtern nach:",
        reset: "Zurücksetzen",
        apply: "Anwenden",
        name: "Name",
        commune: "Gemeinde",
        date: "Datum",
        previous: "Zurück",
        next: "Weiter",
        showMore: "Mehr anzeigen",
        showLess: "Weniger anzeigen",
        seeAll: "Alle anzeigen",
        seeMore: "Mehr anzeigen",
        readMore: "Weiterlesen",
        loadMore: "Mehr laden",
        website: "Webseite",
        openingHours: "Öffnungszeiten",
        contact: "Kontakt",
        description: "Beschreibung",
        informations: "Information",
        toggleFilters: "Filter anzeigen/ausblenden",
        clear: "Löschen",
        clearFilters: "Filter löschen",
        list: "Liste",
        map: "Karte",
        agenda: "Agenda",
        searchInThisArea: "In diesem Bereich suchen",
        results: "Ergebnisse",
        result: "Ergebnis",
        duration: "Dauer",
        free: "Kostenlos",
        animals: "Tiere",
        animalsAccepted: "Tiere akzeptiert.",
        animalsAcceptedWithSupplement: "Tiere akzeptiert, mit Aufpreis.",
        animalsAcceptedWithoutSupplement: "Tiere akzeptiert, ohne Aufpreis.",
        animalsNotAccepted: "Tiere nicht akzeptiert.",
        roomRental: "Saalvermietung",
        modularRoomsCount: "Anzahl modularer Säle",
        equippedMeetingRooms: "Ausgestattete Besprechungsräume",
        maxCapacity: "Max. Kapazität",
        accessibility: "Barrierefreiheit",
        tourismAndDisability: "Tourismus und Behinderung",
        viewAllAccessibilityCriteria:
          "Alle Barrierefreiheitskriterien anzeigen",
        descriptifHandicapMoteur: "Beschreibung motorische Behinderung",
        descriptifHandicapAuditif: "Beschreibung Hörbehinderung",
        descriptifHandicapVisuel: "Beschreibung Sehbehinderung",
        descriptifHandicapMental: "Beschreibung geistige Behinderung",
        classification: "Klassifizierung",
        activities: "Aktivitäten",
        type: "Typ",
        audience: "Publikum",
        labels: "Labels",
        labelsAndChaines: "Labels & Ketten",
        extraDocumentation: "Zusätzliche Dokumentation",
        photoVisit: "Fotorundgang",
        backToFiche: "Zurück zur Karte",
        distance: "Entfernung",
        itinerary: "Strecke",
        altitudeMaximum: "Max. Höhe",
        altitudeMinimum: "Min. Höhe",
        altitude: "Höhe",
        denivellationPositive: "Positiver Höhenunterschied",
        denivellationNegative: "Negativer Höhenunterschied",
        precisionsBalisage: "Wegmarkierungsangaben",
        balisage: "Wegmarkierung",
        opening: "Öffnung",
        openings: "Öffnungszeiten",
        pricing: "Preise",
        paymentMethods: "Zahlungsmittel",
        exceptionalClosurePrefix: "Außerordentliche Schließung ",
        closed: "Geschlossen",
        open: "Geöffnet",
        reservation: "Reservierung",
        reserve: "Reservieren",
        viewOnMap: "Auf Karte anzeigen",
        dateStartPlaceholder: "Startdatum",
        dateEndPlaceholder: "Enddatum",
        communePlaceholder: "Gemeindename",
        selected: "ausgewählt",
        selectedPlural: "ausgewählt",
        dateFrom: "Von",
        dateTo: "Bis",
        periodFrom: "Vom",
        periodTo: "bis",
        dayOrdinal1: "1.",
        dayOrdinal2: "2.",
        dayOrdinal3: "3.",
        exceptionalClosurePrefixSingular: "Außerordentliche Schließung am ",
        exceptionalClosurePrefixPlural: "Außerordentliche Schließung am ",
        clearDateStart: "Startdatum löschen",
        clearDateEnd: "Enddatum löschen",
        clearSearch: "Suche löschen",
        noResultsFound: "Keine Ergebnisse gefunden.",
        adjustSearchWithFilters: "Passen Sie Ihre Suche mit den Filtern an.",
        noEventsWithDate: "Keine Veranstaltungen mit verfügbarem Datum",
        viewDetail: "Details anzeigen",
        widgetErrorTitle: "Fehler beim Abrufen des Apidae-Widgets.",
        widgetErrorDescription:
          "Dieses Widget ist für diese Website nicht autorisiert. Bitte ändern Sie die Konfiguration oder die Integration.",
        capabilities: "Kapazitäten",
        capacitesUnitesHebergementDeclarees: "Gemeldete Unterkunftseinheiten",
        capacitesPersonnesDeclarees: "Gemeldete Kapazität (Personen)",
        capacitesNombreChambres: "Anzahl der Zimmer",
        capacitesNombreAppartements: "Anzahl der Wohnungen",
        capacitesNombreDortoirs: "Anzahl der Schlafsäle",
        capacitesNombreVillas: "Anzahl der Villen",
        capacitesPersonnesClassees: "Klassifizierte Kapazität (Personen)",
        capacitesNombreCabines: "Anzahl der Kabinen",
        capacitesNombreLitsDoubles: "Anzahl der Doppelbetten",
        capacitesNombreLitsSimples: "Anzahl der Einzelbetten",
        capacitesSurface: "Fläche",
        capacitesNombreSallesDeBain: "Anzahl der Badezimmer",
        capacitesUnitesHebergementClassees:
          "Klassifizierte Unterkunftseinheiten",
        capacitesSuperficie: "Grundfläche",
        capacitesNombreChambresSimples: "Anzahl der Einzelzimmer",
        capacitesNombreChambresDoubles: "Anzahl der Doppelzimmer",
        capacitesNombreSuites: "Anzahl der Suiten",
        capacitesPmrUnitesHebergement:
          "Unterkunftseinheiten für Menschen mit eingeschränkter Mobilität",
        capacitesUnitHebergements: "Unterkunftseinheiten",
        capacitesUnitPersonnes: "Personen",
        capacitesUnitChambres: "Zimmer",
        capacitesUnitAppartements: "Wohnungen",
        capacitesUnitDortoirs: "Schlafsäle",
        capacitesUnitVillas: "Villen",
        capacitesUnitCabines: "Kabinen",
        capacitesUnitLits: "Betten",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "Badezimmer",
        capacitesUnitEmplacements: "Stellplätze",
        capacitesUnitSuites: "Suiten",
      },
      // Autres langues avec fallback sur l'anglais
      [WidgetLanguage.NL]: {
        loading: "Laden...",
        error: "Fout",
        noResults: "Geen resultaten",
        sortBy: "Sorteren op",
        filters: "Filters",
        filterBy: "Filteren op:",
        reset: "Resetten",
        apply: "Toepassen",
        name: "Naam",
        commune: "Gemeente",
        date: "Datum",
        previous: "Vorige",
        next: "Volgende",
        showMore: "Toon meer",
        showLess: "Toon minder",
        seeAll: "Alles tonen",
        seeMore: "Meer tonen",
        readMore: "Lees meer",
        loadMore: "Meer laden",
        website: "Website",
        openingHours: "Openingstijden",
        contact: "Contact",
        description: "Beschrijving",
        informations: "Informatie",
        toggleFilters: "Filters tonen/verbergen",
        clear: "Wissen",
        clearFilters: "Filters wissen",
        list: "Lijst",
        map: "Kaart",
        agenda: "Agenda",
        searchInThisArea: "Zoeken in dit gebied",
        results: "Resultaten",
        result: "Resultaat",
        duration: "Duur",
        free: "Gratis",
        animals: "Dieren",
        animalsAccepted: "Dieren toegestaan.",
        animalsAcceptedWithSupplement: "Dieren toegestaan, met toeslag.",
        animalsAcceptedWithoutSupplement: "Dieren toegestaan, zonder toeslag.",
        animalsNotAccepted: "Dieren niet toegestaan.",
        roomRental: "Zaalverhuur",
        modularRoomsCount: "Aantal modulaire zalen",
        equippedMeetingRooms: "Uitgeruste vergaderzalen",
        maxCapacity: "Max. capaciteit",
        accessibility: "Toegankelijkheid",
        tourismAndDisability: "Toerisme en Handicap",
        viewAllAccessibilityCriteria: "Alle toegankelijkheidscriteria bekijken",
        descriptifHandicapMoteur: "Beschrijving motorische handicap",
        descriptifHandicapAuditif: "Beschrijving auditieve handicap",
        descriptifHandicapVisuel: "Beschrijving visuele handicap",
        descriptifHandicapMental: "Beschrijving verstandelijke handicap",
        classification: "Classificatie",
        activities: "Activiteiten",
        type: "Type",
        audience: "Publiek",
        labels: "Labels",
        labelsAndChaines: "Labels & ketens",
        extraDocumentation: "Extra documentatie",
        photoVisit: "Fotorondleiding",
        backToFiche: "Terug naar de fiche",
        distance: "Afstand",
        itinerary: "Route",
        altitudeMaximum: "Max. hoogte",
        altitudeMinimum: "Min. hoogte",
        altitude: "Hoogte",
        denivellationPositive: "Positief hoogteverschil",
        denivellationNegative: "Negatief hoogteverschil",
        precisionsBalisage: "Details markering",
        balisage: "Markering",
        opening: "Opening",
        openings: "Openingsuren",
        pricing: "Prijzen",
        paymentMethods: "Betaalmethoden",
        exceptionalClosurePrefix: "Uitzonderlijke sluiting ",
        closed: "Gesloten",
        open: "Open",
        reservation: "Reservering",
        reserve: "Reserveren",
        viewOnMap: "Bekijk op kaart",
        dateStartPlaceholder: "Startdatum",
        dateEndPlaceholder: "Einddatum",
        communePlaceholder: "Gemeentenaam",
        selected: "geselecteerd",
        selectedPlural: "geselecteerd",
        dateFrom: "Van",
        dateTo: "Tot",
        periodFrom: "Van",
        periodTo: "tot",
        dayOrdinal1: "1e",
        dayOrdinal2: "2e",
        dayOrdinal3: "3e",
        exceptionalClosurePrefixSingular: "Uitzonderlijke sluiting op ",
        exceptionalClosurePrefixPlural: "Uitzonderlijke sluiting op ",
        clearDateStart: "Startdatum wissen",
        clearDateEnd: "Einddatum wissen",
        clearSearch: "Zoekopdracht wissen",
        noResultsFound: "Geen resultaten gevonden.",
        adjustSearchWithFilters: "Pas uw zoekopdracht aan met de filters.",
        noEventsWithDate: "Geen evenementen met beschikbare datum",
        viewDetail: "Details bekijken",
        widgetErrorTitle: "Fout bij het ophalen van de Apidae-widget.",
        widgetErrorDescription:
          "Deze widget is niet geautoriseerd voor deze website. Wijzig de configuratie of de integratie.",
        capabilities: "Capaciteiten",
        capacitesUnitesHebergementDeclarees:
          "Aantal gemelde accommodatie-eenheden",
        capacitesPersonnesDeclarees: "Gemelde capaciteit (personen)",
        capacitesNombreChambres: "Aantal kamers",
        capacitesNombreAppartements: "Aantal appartementen",
        capacitesNombreDortoirs: "Aantal slaapzalen",
        capacitesNombreVillas: "Aantal villa's",
        capacitesPersonnesClassees: "Geclassificeerde capaciteit (personen)",
        capacitesNombreCabines: "Aantal hutten",
        capacitesNombreLitsDoubles: "Aantal tweepersoonsbedden",
        capacitesNombreLitsSimples: "Aantal eenpersoonsbedden",
        capacitesSurface: "Oppervlakte",
        capacitesNombreSallesDeBain: "Aantal badkamers",
        capacitesUnitesHebergementClassees:
          "Geclassificeerde accommodatie-eenheden",
        capacitesSuperficie: "Oppervlakte",
        capacitesNombreChambresSimples: "Aantal eenpersoonskamers",
        capacitesNombreChambresDoubles: "Aantal tweepersoonskamers",
        capacitesNombreSuites: "Aantal suites",
        capacitesPmrUnitesHebergement:
          "Accommodatie-eenheden voor personen met beperkte mobiliteit",
        capacitesUnitHebergements: "accommodatie-eenheden",
        capacitesUnitPersonnes: "personen",
        capacitesUnitChambres: "kamers",
        capacitesUnitAppartements: "appartementen",
        capacitesUnitDortoirs: "slaapzalen",
        capacitesUnitVillas: "villa's",
        capacitesUnitCabines: "hutten",
        capacitesUnitLits: "bedden",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "badkamers",
        capacitesUnitEmplacements: "plaatsen",
        capacitesUnitSuites: "suites",
      },
      [WidgetLanguage.RU]: {
        loading: "Загрузка...",
        error: "Ошибка",
        noResults: "Нет результатов",
        sortBy: "Сортировать по",
        filters: "Фильтры",
        filterBy: "Фильтровать по:",
        reset: "Сбросить",
        apply: "Применить",
        name: "Название",
        commune: "Город",
        date: "Дата",
        previous: "Предыдущий",
        next: "Следующий",
        showMore: "Показать больше",
        showLess: "Показать меньше",
        seeAll: "Показать всё",
        seeMore: "Показать ещё",
        readMore: "Читать далее",
        loadMore: "Загрузить больше",
        website: "Веб-сайт",
        openingHours: "Часы работы",
        contact: "Контакты",
        description: "Описание",
        informations: "Информация",
        toggleFilters: "Показать/Скрыть фильтры",
        clear: "Очистить",
        clearFilters: "Очистить фильтры",
        list: "Список",
        map: "Карта",
        agenda: "Повестка дня",
        searchInThisArea: "Искать в этой области",
        results: "Результаты",
        result: "Результат",
        duration: "Продолжительность",
        free: "Бесплатно",
        animals: "Животные",
        animalsAccepted: "Животные принимаются.",
        animalsAcceptedWithSupplement: "Животные принимаются, с доплатой.",
        animalsAcceptedWithoutSupplement: "Животные принимаются, без доплаты.",
        animalsNotAccepted: "Животные не принимаются.",
        roomRental: "Аренда залов",
        modularRoomsCount: "Количество модульных залов",
        equippedMeetingRooms: "Оборудованные залы для совещаний",
        maxCapacity: "Макс. вместимость",
        accessibility: "Доступность",
        tourismAndDisability: "Туризм и инвалидность",
        viewAllAccessibilityCriteria: "Показать все критерии доступности",
        descriptifHandicapMoteur: "Описание для инвалидов-колясочников",
        descriptifHandicapAuditif: "Описание для слабослышащих",
        descriptifHandicapVisuel: "Описание для слабовидящих",
        descriptifHandicapMental:
          "Описание для лиц с ментальными особенностями",
        classification: "Классификация",
        activities: "Мероприятия",
        type: "Тип",
        audience: "Аудитория",
        labels: "Метки",
        labelsAndChaines: "Метки и сети",
        extraDocumentation: "Дополнительная документация",
        photoVisit: "Фотогалерея",
        backToFiche: "Вернуться к карточке",
        distance: "Расстояние",
        itinerary: "Маршрут",
        altitudeMaximum: "Макс. высота",
        altitudeMinimum: "Мин. высота",
        altitude: "Высота",
        denivellationPositive: "Положительный перепад",
        denivellationNegative: "Отрицательный перепад",
        precisionsBalisage: "Уточнения маркировки",
        balisage: "Маркировка",
        opening: "Открытие",
        openings: "Часы работы",
        pricing: "Тарифы",
        paymentMethods: "Способы оплаты",
        exceptionalClosurePrefix: "Исключительное закрытие",
        closed: "Закрыто",
        open: "Открыто",
        reservation: "Бронирование",
        reserve: "Забронировать",
        viewOnMap: "Показать на карте",
        dateStartPlaceholder: "Дата начала",
        dateEndPlaceholder: "Дата окончания",
        communePlaceholder: "Название города",
        selected: "выбрано",
        selectedPlural: "выбрано",
        dateFrom: "От",
        dateTo: "До",
        periodFrom: "С",
        periodTo: "до",
        dayOrdinal1: "1-е",
        dayOrdinal2: "2-е",
        dayOrdinal3: "3-е",
        exceptionalClosurePrefixSingular: "Исключительное закрытие ",
        exceptionalClosurePrefixPlural: "Исключительное закрытие ",
        clearDateStart: "Очистить дату начала",
        clearDateEnd: "Очистить дату окончания",
        clearSearch: "Очистить поиск",
        noResultsFound: "Результаты не найдены.",
        adjustSearchWithFilters: "Настройте поиск с помощью фильтров.",
        noEventsWithDate: "Нет событий с доступной датой",
        viewDetail: "Просмотр деталей",
        widgetErrorTitle: "Ошибка получения виджета Apidae.",
        widgetErrorDescription:
          "Этот виджет не авторизован для этого веб-сайта. Пожалуйста, измените его конфигурацию или измените его интеграцию.",
        capabilities: "Вместимость",
        capacitesUnitesHebergementDeclarees: "Заявленные единицы размещения",
        capacitesPersonnesDeclarees: "Заявленная вместимость (человек)",
        capacitesNombreChambres: "Количество номеров",
        capacitesNombreAppartements: "Количество апартаментов",
        capacitesNombreDortoirs: "Количество спален",
        capacitesNombreVillas: "Количество вилл",
        capacitesPersonnesClassees: "Классифицированная вместимость (человек)",
        capacitesNombreCabines: "Количество кают",
        capacitesNombreLitsDoubles: "Количество двуспальных кроватей",
        capacitesNombreLitsSimples: "Количество односпальных кроватей",
        capacitesSurface: "Площадь",
        capacitesNombreSallesDeBain: "Количество ванных комнат",
        capacitesUnitesHebergementClassees:
          "Классифицированные единицы размещения",
        capacitesSuperficie: "Площадь",
        capacitesNombreChambresSimples: "Количество одноместных номеров",
        capacitesNombreChambresDoubles: "Количество двухместных номеров",
        capacitesNombreSuites: "Количество люксов",
        capacitesPmrUnitesHebergement:
          "Единицы размещения для людей с ограниченной подвижностью",
        capacitesUnitHebergements: "единицы размещения",
        capacitesUnitPersonnes: "человек",
        capacitesUnitChambres: "номеров",
        capacitesUnitAppartements: "апартаментов",
        capacitesUnitDortoirs: "спален",
        capacitesUnitVillas: "вилл",
        capacitesUnitCabines: "кают",
        capacitesUnitLits: "кроватей",
        capacitesUnitM2: "м²",
        capacitesUnitSallesDeBain: "ванных комнат",
        capacitesUnitEmplacements: "мест",
        capacitesUnitSuites: "люксов",
      },
      [WidgetLanguage.ZH]: {
        loading: "加载中...",
        error: "错误",
        noResults: "无结果",
        sortBy: "排序方式",
        filters: "筛选",
        filterBy: "筛选条件：",
        reset: "重置",
        apply: "应用",
        name: "名称",
        commune: "城市",
        date: "日期",
        previous: "上一页",
        next: "下一页",
        showMore: "显示更多",
        showLess: "显示更少",
        seeAll: "查看全部",
        seeMore: "查看更多",
        readMore: "阅读更多",
        loadMore: "加载更多",
        website: "网站",
        openingHours: "营业时间",
        contact: "联系方式",
        description: "描述",
        informations: "信息",
        toggleFilters: "显示/隐藏筛选",
        clear: "清除",
        clearFilters: "清除筛选",
        list: "列表",
        map: "地图",
        agenda: "日程",
        searchInThisArea: "在此区域搜索",
        results: "结果",
        result: "结果",
        duration: "时长",
        free: "免费",
        animals: "动物",
        animalsAccepted: "接受动物。",
        animalsAcceptedWithSupplement: "接受动物，需支付附加费。",
        animalsAcceptedWithoutSupplement: "接受动物，无需附加费。",
        animalsNotAccepted: "不接受动物。",
        roomRental: "会议室租赁",
        modularRoomsCount: "可组合会议室数量",
        equippedMeetingRooms: "配备齐全的会议室",
        maxCapacity: "最大容纳人数",
        accessibility: "无障碍",
        tourismAndDisability: "旅游与残障",
        viewAllAccessibilityCriteria: "查看所有无障碍标准",
        descriptifHandicapMoteur: "行动障碍说明",
        descriptifHandicapAuditif: "听力障碍说明",
        descriptifHandicapVisuel: "视力障碍说明",
        descriptifHandicapMental: "精神障碍说明",
        classification: "分类",
        activities: "活动",
        type: "类型",
        audience: "受众",
        labels: "标签",
        labelsAndChaines: "标签与连锁",
        extraDocumentation: "补充文档",
        photoVisit: "照片导览",
        backToFiche: "返回列表",
        distance: "距离",
        itinerary: "路线",
        altitudeMaximum: "最高海拔",
        altitudeMinimum: "最低海拔",
        altitude: "海拔",
        denivellationPositive: "正爬升",
        denivellationNegative: "负爬升",
        precisionsBalisage: "路标说明",
        balisage: "路标",
        opening: "开放",
        openings: "开放时间",
        pricing: "价格",
        paymentMethods: "支付方式",
        exceptionalClosurePrefix: "例外关闭",
        closed: "关闭",
        open: "开放",
        reservation: "预订",
        reserve: "预订",
        viewOnMap: "在地图上查看",
        dateStartPlaceholder: "开始日期",
        dateEndPlaceholder: "结束日期",
        communePlaceholder: "城市名称",
        selected: "已选择",
        selectedPlural: "已选择",
        dateFrom: "从",
        dateTo: "到",
        periodFrom: "从",
        periodTo: "到",
        dayOrdinal1: "1日",
        dayOrdinal2: "2日",
        dayOrdinal3: "3日",
        exceptionalClosurePrefixSingular: "例外关闭",
        exceptionalClosurePrefixPlural: "例外关闭",
        clearDateStart: "清除开始日期",
        clearDateEnd: "清除结束日期",
        clearSearch: "清除搜索",
        noResultsFound: "未找到结果。",
        adjustSearchWithFilters: "使用筛选器调整您的搜索。",
        noEventsWithDate: "没有可用日期的事件",
        viewDetail: "查看详情",
        widgetErrorTitle: "检索 Apidae 小部件时出错。",
        widgetErrorDescription:
          "此小部件未授权用于此网站。请修改其配置或修改其集成。",
        capabilities: "容量",
        capacitesUnitesHebergementDeclarees: "申报的住宿单位数",
        capacitesPersonnesDeclarees: "申报容量（人数）",
        capacitesNombreChambres: "房间数",
        capacitesNombreAppartements: "公寓数",
        capacitesNombreDortoirs: "宿舍数",
        capacitesNombreVillas: "别墅数",
        capacitesPersonnesClassees: "分类容量（人数）",
        capacitesNombreCabines: "小屋数",
        capacitesNombreLitsDoubles: "双人床数",
        capacitesNombreLitsSimples: "单人床数",
        capacitesSurface: "面积",
        capacitesNombreSallesDeBain: "浴室数",
        capacitesUnitesHebergementClassees: "分类住宿单位数",
        capacitesSuperficie: "面积",
        capacitesNombreChambresSimples: "单人间数",
        capacitesNombreChambresDoubles: "双人间数",
        capacitesNombreSuites: "套房数",
        capacitesPmrUnitesHebergement: "无障碍住宿单位数",
        capacitesUnitHebergements: "住宿单位",
        capacitesUnitPersonnes: "人",
        capacitesUnitChambres: "间客房",
        capacitesUnitAppartements: "套公寓",
        capacitesUnitDortoirs: "个宿舍",
        capacitesUnitVillas: "栋别墅",
        capacitesUnitCabines: "间小屋",
        capacitesUnitLits: "张床",
        capacitesUnitM2: "平方米",
        capacitesUnitSallesDeBain: "间浴室",
        capacitesUnitEmplacements: "个营位",
        capacitesUnitSuites: "间套房",
      },
      [WidgetLanguage.PT_BR]: {
        loading: "Carregando...",
        error: "Erro",
        noResults: "Nenhum resultado",
        sortBy: "Ordenar por",
        filters: "Filtros",
        filterBy: "Filtrar por:",
        reset: "Redefinir",
        apply: "Aplicar",
        name: "Nome",
        commune: "Cidade",
        date: "Data",
        previous: "Anterior",
        next: "Próximo",
        showMore: "Mostrar mais",
        showLess: "Mostrar menos",
        seeAll: "Ver tudo",
        seeMore: "Ver mais",
        readMore: "Ler mais",
        loadMore: "Carregar mais",
        website: "Site",
        openingHours: "Horário de funcionamento",
        contact: "Contato",
        description: "Descrição",
        informations: "Informação",
        toggleFilters: "Mostrar/Ocultar filtros",
        clear: "Limpar",
        clearFilters: "Limpar filtros",
        list: "Lista",
        map: "Mapa",
        agenda: "Agenda",
        searchInThisArea: "Buscar nesta área",
        results: "Resultados",
        result: "Resultado",
        duration: "Duração",
        free: "Gratuito",
        animals: "Animais",
        animalsAccepted: "Animais aceitos.",
        animalsAcceptedWithSupplement: "Animais aceitos, com suplemento.",
        animalsAcceptedWithoutSupplement: "Animais aceitos, sem suplemento.",
        animalsNotAccepted: "Animais não aceitos.",
        roomRental: "Aluguel de salas",
        modularRoomsCount: "Número de salas modulares",
        equippedMeetingRooms: "Salas de reunião equipadas",
        maxCapacity: "Capacidade máxima",
        accessibility: "Acessibilidade",
        tourismAndDisability: "Turismo e Deficiência",
        viewAllAccessibilityCriteria:
          "Ver todos os critérios de acessibilidade",
        descriptifHandicapMoteur: "Descrição deficiência motora",
        descriptifHandicapAuditif: "Descrição deficiência auditiva",
        descriptifHandicapVisuel: "Descrição deficiência visual",
        descriptifHandicapMental: "Descrição deficiência mental",
        classification: "Classificação",
        activities: "Atividades",
        type: "Tipo",
        audience: "Público",
        labels: "Etiquetas",
        labelsAndChaines: "Etiquetas e cadeias",
        extraDocumentation: "Documentação adicional",
        photoVisit: "Visita fotos",
        backToFiche: "Voltar à ficha",
        distance: "Distância",
        itinerary: "Itinerário",
        altitudeMaximum: "Altitude máx.",
        altitudeMinimum: "Altitude mín.",
        altitude: "Altitude",
        denivellationPositive: "Desnível positivo",
        denivellationNegative: "Desnível negativo",
        precisionsBalisage: "Precisões de sinalização",
        balisage: "Sinalização",
        opening: "Abertura",
        openings: "Horário de funcionamento",
        pricing: "Preços",
        paymentMethods: "Métodos de pagamento",
        exceptionalClosurePrefix: "Encerramento excecional ",
        closed: "Fechado",
        open: "Aberto",
        reservation: "Reserva",
        reserve: "Reservar",
        viewOnMap: "Ver no mapa",
        dateStartPlaceholder: "Data de início",
        dateEndPlaceholder: "Data de término",
        communePlaceholder: "Nome da cidade",
        selected: "selecionado",
        selectedPlural: "selecionados",
        dateFrom: "De",
        dateTo: "Até",
        periodFrom: "Do",
        periodTo: "ao",
        dayOrdinal1: "1.º",
        dayOrdinal2: "2.º",
        dayOrdinal3: "3.º",
        exceptionalClosurePrefixSingular: "Encerramento excecional em ",
        exceptionalClosurePrefixPlural: "Encerramento excecional em ",
        clearDateStart: "Limpar data de início",
        clearDateEnd: "Limpar data de término",
        clearSearch: "Limpar pesquisa",
        noResultsFound: "Nenhum resultado encontrado.",
        adjustSearchWithFilters: "Ajuste sua pesquisa usando os filtros.",
        noEventsWithDate: "Nenhum evento com data disponível",
        viewDetail: "Ver detalhes",
        widgetErrorTitle: "Erro ao recuperar o widget Apidae.",
        widgetErrorDescription:
          "Este widget não está autorizado para este site. Por favor, modifique sua configuração ou modifique sua integração.",
        capabilities: "Capacidades",
        capacitesUnitesHebergementDeclarees:
          "Unidades de alojamento declaradas",
        capacitesPersonnesDeclarees: "Capacidade declarada (pessoas)",
        capacitesNombreChambres: "Número de quartos",
        capacitesNombreAppartements: "Número de apartamentos",
        capacitesNombreDortoirs: "Número de dormitórios",
        capacitesNombreVillas: "Número de vilas",
        capacitesPersonnesClassees: "Capacidade classificada (pessoas)",
        capacitesNombreCabines: "Número de cabines",
        capacitesNombreLitsDoubles: "Número de camas de casal",
        capacitesNombreLitsSimples: "Número de camas de solteiro",
        capacitesSurface: "Área",
        capacitesNombreSallesDeBain: "Número de casas de banho",
        capacitesUnitesHebergementClassees:
          "Unidades de alojamento classificadas",
        capacitesSuperficie: "Área",
        capacitesNombreChambresSimples: "Número de quartos individuais",
        capacitesNombreChambresDoubles: "Número de quartos duplos",
        capacitesNombreSuites: "Número de suítes",
        capacitesPmrUnitesHebergement:
          "Unidades de alojamento para pessoas com mobilidade reduzida",
        capacitesUnitHebergements: "unidades de alojamento",
        capacitesUnitPersonnes: "pessoas",
        capacitesUnitChambres: "quartos",
        capacitesUnitAppartements: "apartamentos",
        capacitesUnitDortoirs: "dormitórios",
        capacitesUnitVillas: "vilas",
        capacitesUnitCabines: "cabines",
        capacitesUnitLits: "camas",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "casas de banho",
        capacitesUnitEmplacements: "lugares",
        capacitesUnitSuites: "suítes",
      },
      [WidgetLanguage.JA]: {
        loading: "読み込み中...",
        error: "エラー",
        noResults: "結果なし",
        sortBy: "並び替え",
        filters: "フィルター",
        filterBy: "フィルター条件：",
        reset: "リセット",
        apply: "適用",
        name: "名前",
        commune: "都市",
        date: "日付",
        previous: "前へ",
        next: "次へ",
        showMore: "もっと見る",
        showLess: "折りたたむ",
        seeAll: "すべて見る",
        seeMore: "もっと見る",
        readMore: "続きを読む",
        loadMore: "さらに読み込む",
        website: "ウェブサイト",
        openingHours: "営業時間",
        contact: "連絡先",
        description: "説明",
        informations: "情報",
        toggleFilters: "フィルター表示/非表示",
        clear: "クリア",
        clearFilters: "フィルターをクリア",
        list: "リスト",
        map: "地図",
        agenda: "予定",
        searchInThisArea: "このエリアで検索",
        results: "結果",
        result: "結果",
        duration: "期間",
        free: "無料",
        animals: "動物",
        animalsAccepted: "動物受け入れ。",
        animalsAcceptedWithSupplement: "動物受け入れ、追加料金あり。",
        animalsAcceptedWithoutSupplement: "動物受け入れ、追加料金なし。",
        animalsNotAccepted: "動物受け入れ不可。",
        roomRental: "会場レンタル",
        modularRoomsCount: "モジュール式会場数",
        equippedMeetingRooms: "設備完備の会議室",
        maxCapacity: "最大収容人数",
        accessibility: "アクセシビリティ",
        tourismAndDisability: "観光と障がい",
        viewAllAccessibilityCriteria: "すべてのアクセシビリティ基準を表示",
        descriptifHandicapMoteur: "肢体不自由の説明",
        descriptifHandicapAuditif: "聴覚障害の説明",
        descriptifHandicapVisuel: "視覚障害の説明",
        descriptifHandicapMental: "知的障害の説明",
        classification: "分類",
        activities: "活動",
        type: "タイプ",
        audience: "対象",
        labels: "ラベル",
        labelsAndChaines: "ラベル・チェーン",
        extraDocumentation: "追加資料",
        photoVisit: "フォトツアー",
        backToFiche: "一覧に戻る",
        distance: "距離",
        itinerary: "ルート",
        altitudeMaximum: "最大標高",
        altitudeMinimum: "最小標高",
        altitude: "標高",
        denivellationPositive: "累積登り",
        denivellationNegative: "累積下り",
        precisionsBalisage: "標識の詳細",
        balisage: "標識",
        opening: "開館",
        openings: "営業時間",
        exceptionalClosurePrefix: "臨時休業 ",
        closed: "休業",
        open: "営業中",
        pricing: "料金",
        paymentMethods: "支払い方法",
        reservation: "予約",
        reserve: "予約する",
        viewOnMap: "地図で見る",
        dateStartPlaceholder: "開始日",
        dateEndPlaceholder: "終了日",
        communePlaceholder: "都市名",
        selected: "選択済み",
        selectedPlural: "選択済み",
        dateFrom: "から",
        dateTo: "まで",
        periodFrom: "から",
        periodTo: "まで",
        dayOrdinal1: "1日",
        dayOrdinal2: "2日",
        dayOrdinal3: "3日",
        exceptionalClosurePrefixSingular: "臨時休業",
        exceptionalClosurePrefixPlural: "臨時休業",
        clearDateStart: "開始日をクリア",
        clearDateEnd: "終了日をクリア",
        clearSearch: "検索をクリア",
        noResultsFound: "結果が見つかりませんでした。",
        adjustSearchWithFilters: "フィルターを使用して検索を調整してください。",
        noEventsWithDate: "利用可能な日付のイベントがありません",
        viewDetail: "詳細を見る",
        widgetErrorTitle: "Apidaeウィジェットの取得エラー。",
        widgetErrorDescription:
          "このウィジェットはこのウェブサイトでは許可されていません。設定を変更するか、統合を変更してください。",
        capabilities: "収容人数",
        capacitesUnitesHebergementDeclarees: "申告宿泊単位数",
        capacitesPersonnesDeclarees: "申告収容人数",
        capacitesNombreChambres: "客室数",
        capacitesNombreAppartements: "アパート数",
        capacitesNombreDortoirs: "ドミトリー数",
        capacitesNombreVillas: "ヴィラ数",
        capacitesPersonnesClassees: "認定収容人数",
        capacitesNombreCabines: "キャビン数",
        capacitesNombreLitsDoubles: "ダブルベッド数",
        capacitesNombreLitsSimples: "シングルベッド数",
        capacitesSurface: "面積",
        capacitesNombreSallesDeBain: "バスルーム数",
        capacitesUnitesHebergementClassees: "認定宿泊単位数",
        capacitesSuperficie: "面積",
        capacitesNombreChambresSimples: "シングルルーム数",
        capacitesNombreChambresDoubles: "ツイン/ダブルルーム数",
        capacitesNombreSuites: "スイート数",
        capacitesPmrUnitesHebergement: "車椅子対応宿泊単位数",
        capacitesUnitHebergements: "宿泊単位",
        capacitesUnitPersonnes: "人",
        capacitesUnitChambres: "室",
        capacitesUnitAppartements: "アパート",
        capacitesUnitDortoirs: "ドミトリー",
        capacitesUnitVillas: "ヴィラ",
        capacitesUnitCabines: "キャビン",
        capacitesUnitLits: "ベッド",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "バスルーム",
        capacitesUnitEmplacements: "サイト",
        capacitesUnitSuites: "スイート",
      },
      [WidgetLanguage.KO]: {
        loading: "로딩 중...",
        error: "오류",
        noResults: "결과 없음",
        sortBy: "정렬 기준",
        filters: "필터",
        filterBy: "필터 조건:",
        reset: "재설정",
        apply: "적용",
        name: "이름",
        commune: "도시",
        date: "날짜",
        previous: "이전",
        next: "다음",
        showMore: "더 보기",
        showLess: "접기",
        seeAll: "전체 보기",
        seeMore: "더 보기",
        readMore: "자세히 보기",
        loadMore: "더 불러오기",
        website: "웹사이트",
        openingHours: "영업 시간",
        contact: "연락처",
        description: "설명",
        informations: "정보",
        toggleFilters: "필터 표시/숨기기",
        clear: "지우기",
        clearFilters: "필터 지우기",
        list: "목록",
        map: "지도",
        agenda: "일정",
        searchInThisArea: "이 영역에서 검색",
        results: "결과",
        result: "결과",
        duration: "기간",
        free: "무료",
        animals: "동물",
        animalsAccepted: "동물 허용.",
        animalsAcceptedWithSupplement: "동물 허용, 추가 요금 있음.",
        animalsAcceptedWithoutSupplement: "동물 허용, 추가 요금 없음.",
        animalsNotAccepted: "동물 허용 안 함.",
        roomRental: "회의실 대여",
        modularRoomsCount: "모듈형 회의실 수",
        equippedMeetingRooms: "비품 완비 회의실",
        maxCapacity: "최대 수용 인원",
        accessibility: "접근성",
        tourismAndDisability: "관광과 장애",
        viewAllAccessibilityCriteria: "모든 접근성 기준 보기",
        descriptifHandicapMoteur: "지체 장애 설명",
        descriptifHandicapAuditif: "청각 장애 설명",
        descriptifHandicapVisuel: "시각 장애 설명",
        descriptifHandicapMental: "지적 장애 설명",
        classification: "분류",
        activities: "활동",
        type: "유형",
        audience: "대상",
        labels: "라벨",
        labelsAndChaines: "라벨 및 체인",
        extraDocumentation: "추가 문서",
        photoVisit: "사진 투어",
        backToFiche: "목록으로 돌아가기",
        distance: "거리",
        itinerary: "경로",
        altitudeMaximum: "최대 고도",
        altitudeMinimum: "최소 고도",
        altitude: "고도",
        denivellationPositive: "상승 고도",
        denivellationNegative: "하강 고도",
        precisionsBalisage: "표지판 세부정보",
        balisage: "표지판",
        opening: "개방",
        openings: "운영 시간",
        pricing: "요금",
        paymentMethods: "결제 수단",
        exceptionalClosurePrefix: "예외 휴무",
        closed: "휴무",
        open: "영업",
        reservation: "예약",
        reserve: "예약하기",
        viewOnMap: "지도에서 보기",
        dateStartPlaceholder: "시작일",
        dateEndPlaceholder: "종료일",
        communePlaceholder: "도시 이름",
        selected: "선택됨",
        selectedPlural: "선택됨",
        dateFrom: "부터",
        dateTo: "까지",
        periodFrom: "부터",
        periodTo: "까지",
        dayOrdinal1: "1일",
        dayOrdinal2: "2일",
        dayOrdinal3: "3일",
        exceptionalClosurePrefixSingular: "예외 휴무",
        exceptionalClosurePrefixPlural: "예외 휴무",
        clearDateStart: "시작일 지우기",
        clearDateEnd: "종료일 지우기",
        clearSearch: "검색 지우기",
        noResultsFound: "결과를 찾을 수 없습니다.",
        adjustSearchWithFilters: "필터를 사용하여 검색을 조정하세요.",
        noEventsWithDate: "사용 가능한 날짜가 있는 이벤트 없음",
        viewDetail: "자세히 보기",
        widgetErrorTitle: "Apidae 위젯을 가져오는 중 오류가 발생했습니다.",
        widgetErrorDescription:
          "이 위젯은 이 웹사이트에 대해 승인되지 않았습니다. 구성을 수정하거나 통합을 수정하세요.",
        capabilities: "수용 인원",
        capacitesUnitesHebergementDeclarees: "신고 숙박 단위 수",
        capacitesPersonnesDeclarees: "신고 수용 인원",
        capacitesNombreChambres: "객실 수",
        capacitesNombreAppartements: "아파트 수",
        capacitesNombreDortoirs: "도미토리 수",
        capacitesNombreVillas: "빌라 수",
        capacitesPersonnesClassees: "분류 수용 인원",
        capacitesNombreCabines: "캐빈 수",
        capacitesNombreLitsDoubles: "더블 침대 수",
        capacitesNombreLitsSimples: "싱글 침대 수",
        capacitesSurface: "면적",
        capacitesNombreSallesDeBain: "욕실 수",
        capacitesUnitesHebergementClassees: "분류 숙박 단위 수",
        capacitesSuperficie: "면적",
        capacitesNombreChambresSimples: "싱글 룸 수",
        capacitesNombreChambresDoubles: "더블 룸 수",
        capacitesNombreSuites: "스위트 룸 수",
        capacitesPmrUnitesHebergement: "휠체어 이용 가능 숙박 단위 수",
        capacitesUnitHebergements: "숙박 단위",
        capacitesUnitPersonnes: "명",
        capacitesUnitChambres: "객실",
        capacitesUnitAppartements: "아파트",
        capacitesUnitDortoirs: "도미토리",
        capacitesUnitVillas: "빌라",
        capacitesUnitCabines: "캐빈",
        capacitesUnitLits: "침대",
        capacitesUnitM2: "m²",
        capacitesUnitSallesDeBain: "욕실",
        capacitesUnitEmplacements: "캠핑 사이트",
        capacitesUnitSuites: "스위트",
      },
    };

    return translations[language]?.[key] || key;
  }

  /**
   * Formate une date selon la locale
   */
  static formatDate(
    dateStr: string,
    language: WidgetLanguage,
    options?: Intl.DateTimeFormatOptions,
  ): string {
    const date = new Date(dateStr);
    const locale = this.getLocale(language);

    const defaultOptions: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    return date.toLocaleDateString(locale, options || defaultOptions);
  }

  /**
   * Retourne le code locale pour une langue donnée
   */
  static getLocale(language: WidgetLanguage): string {
    const locales: Record<WidgetLanguage, string> = {
      [WidgetLanguage.FR]: "fr-FR",
      [WidgetLanguage.EN]: "en-US",
      [WidgetLanguage.ES]: "es-ES",
      [WidgetLanguage.IT]: "it-IT",
      [WidgetLanguage.DE]: "de-DE",
      [WidgetLanguage.NL]: "nl-NL",
      [WidgetLanguage.RU]: "ru-RU",
      [WidgetLanguage.ZH]: "zh-CN",
      [WidgetLanguage.PT_BR]: "pt-BR",
      [WidgetLanguage.JA]: "ja-JP",
      [WidgetLanguage.KO]: "ko-KR",
    };

    return locales[language];
  }
}
